import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from '../context/AuthContext';

function MyOrders() {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load only this user's orders from Firestore
  useEffect(() => {
    async function loadOrders() {
      try {
        const q = query(
          collection(db, 'orders'),
          where('userId', '==', currentUser.uid)
        );
        const snapshot = await getDocs(q);
        const results = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

        // Sort by date newest first (client-side avoids needing a composite index)
        results.sort((a, b) =>
          (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
        );

        setOrders(results);
      } catch (err) {
        console.error('Error loading orders:', err);
      }
      setLoading(false);
    }
    loadOrders();
  }, [currentUser]);

  // Format a Firestore Timestamp into a readable date string
  function formatDate(timestamp) {
    if (!timestamp?.seconds) return '—';
    return new Date(timestamp.seconds * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  // Color-coded badge for order status
  function StatusBadge({ status }) {
    const map = {
      delivered: { label: 'Delivered', bg: '#2e7d32', color: 'white' },
      shipped:   { label: 'Shipped',   bg: '#0277bd', color: 'white' },
      pending:   { label: 'Pending',   bg: '#f4f4f4', color: '#555'  }
    };
    const s = map[status] || map.pending;
    return (
      <span
        className="small fw-semibold text-uppercase"
        style={{
          background: s.bg,
          color: s.color,
          padding: '2px 8px',
          borderRadius: '2px',
          letterSpacing: '0.06em',
          fontSize: '0.7rem'
        }}
      >
        {s.label}
      </span>
    );
  }

  return (
    <div className="container py-5">
      <h4 className="fw-bold text-uppercase mb-1" style={{ letterSpacing: '0.08em' }}>
        My Orders
      </h4>
      <p className="text-muted small mb-4">{currentUser.email}</p>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border spinner-border-sm" style={{ color: '#c84b11' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-5">
          <p className="text-muted mb-3">You haven't placed any orders yet.</p>
          <Link to="/products" className="btn btn-piggy px-4">
            Start Shopping
          </Link>
        </div>
      ) : (
        <div>
          {orders.map(order => (
            <div
              key={order.id}
              className="card border-0 shadow-sm mb-3"
              style={{ borderRadius: '2px' }}
            >
              <div className="card-body p-4">

                {/* Order header row */}
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                  <div>
                    <p className="small text-muted mb-0">Order placed</p>
                    <p className="fw-semibold mb-0">{formatDate(order.createdAt)}</p>
                  </div>
                  <div className="text-center">
                    <p className="small text-muted mb-0">Total</p>
                    <p className="fw-bold mb-0" style={{ color: '#111' }}>
                      ${order.total?.toFixed(2)}
                    </p>
                  </div>
                  <div className="text-end">
                    <p className="small text-muted mb-1">Status</p>
                    <StatusBadge status={order.status} />
                  </div>
                </div>

                <hr style={{ borderColor: '#f0f0f0' }} />

                {/* Items list */}
                <div className="mb-2">
                  {order.items?.map((item, idx) => (
                    <div
                      key={idx}
                      className="d-flex align-items-center gap-3 mb-2"
                    >
                      {/* Product image (clickable) */}
                      <Link to={`/product/${item.id}`} style={{ flexShrink: 0 }}>
                        <img
                          src={item.images?.[0] || item.imageUrl || 'https://placehold.co/52x52/f4f4f4/888?text=PC'}
                          alt={item.name}
                          style={{
                            width: '52px',
                            height: '52px',
                            objectFit: 'cover',
                            borderRadius: '2px'
                          }}
                          onError={e => {
                            e.target.src = 'https://placehold.co/52x52/f4f4f4/888?text=PC';
                          }}
                        />
                      </Link>

                      {/* Item details */}
                      <div className="flex-grow-1">
                        <Link
                          to={`/product/${item.id}`}
                          className="fw-semibold text-decoration-none"
                          style={{ color: '#111', fontSize: '0.9rem' }}
                        >
                          {item.name}
                        </Link>
                        <div className="d-flex gap-3 small text-muted mt-1">
                          {item.selectedSize && (
                            <span>
                              Size:{' '}
                              <span className="fw-semibold text-uppercase" style={{ color: '#c84b11' }}>
                                {item.selectedSize}
                              </span>
                            </span>
                          )}
                          <span>Qty: {item.quantity}</span>
                          <span>${item.price?.toFixed(2)} ea.</span>
                        </div>
                      </div>

                      {/* Line total */}
                      <span className="fw-semibold small" style={{ whiteSpace: 'nowrap' }}>
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Price breakdown footer */}
                <div
                  className="d-flex gap-3 flex-wrap mt-3 pt-3 small text-muted"
                  style={{ borderTop: '1px solid #f0f0f0' }}
                >
                  <span>Subtotal: ${order.subtotal?.toFixed(2)}</span>
                  {order.discountCode && (
                    <span style={{ color: '#2e7d32' }}>
                      Discount ({order.discountCode}): −${order.discountAmount?.toFixed(2)}
                    </span>
                  )}
                  <span>Tax: ${order.tax?.toFixed(2)}</span>
                  <span className="fw-bold" style={{ color: '#111' }}>
                    Total: ${order.total?.toFixed(2)}
                  </span>
                </div>

                {/* Order ID (truncated) */}
                <p
                  className="small text-muted mt-2 mb-0"
                  style={{ fontFamily: 'monospace', fontSize: '0.72rem' }}
                >
                  Order ID: {order.id}
                </p>

              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MyOrders;
